import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { Dialog, DialogContent, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  GroupTypeDetails,
  CollectorGroupSettingsInput,
  Collector
} from 'graphql/types';
import { EnabledDisabled } from 'modules/Settings/constant';
import {
  UpdateSettingPreferencesForCollectorSettingInput,
  useUpdateSettingPreferencesForCollectorSettingMutation
} from 'graphql/types';
import { useCollectorSetting } from '../../hooks';
import { CollectorInstallInfo } from '../../CollectorsListPage';
import {
  useCompanyCollectorSettingsForm,
  FormData,
  mapToAccessSope
} from './hooks';
import { CollectorInfo } from './CollectorInfo';
import { CollectorSettingsBody } from './CollectorSettingsBody';
import { CompanyCollectorSettingsDialogSkeleton } from './CompanyCollectorSettingsDialogSkeleton';

export type CompanyCollectorSettingsDialogProps = {
  onClose: () => void;
  open: boolean;
  collectorInfo: Collector;
  enabledGroupTypes: GroupTypeDetails[];
  collectorInstallInfo: CollectorInstallInfo;
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    overflowY: 'hidden'
  },
  container: {
    display: 'flex',
    margin: theme.spacing(-2.5, 0)
  },
  verticalDivider: {
    backgroundColor: theme.palette.grey[100]
  },
  dialogContent: {
    paddingLeft: 0
  }
}));

export const CompanyCollectorSettingsDialog: React.FC<CompanyCollectorSettingsDialogProps> = ({
  onClose,
  open,
  collectorInfo,
  collectorInstallInfo,
  enabledGroupTypes
}: CompanyCollectorSettingsDialogProps) => {
  const { data, loading } = useCollectorSetting({
    installId: collectorInstallInfo!.installId
  });

  const collectorInstallErrors =
    data?.collectorSetting?.item?.installStatus?.errors;
  const {
    formMethods,
    defaultEnabled,
    defaultOptedIn
  } = useCompanyCollectorSettingsForm(data);
  const [
    updateSettingPreferencesMutation,
    { loading: saving }
  ] = useUpdateSettingPreferencesForCollectorSettingMutation();
  const [defaultValues, setDefaultValues] = useState<any>();
  const { control, reset, formState } = formMethods;
  const { isDirty, isSubmitSuccessful } = formState;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...defaultValues });
    }
  }, [reset, defaultValues, isSubmitSuccessful]);

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'rows'
  });
  const classes = useStyles({ rows: fields.length });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      const {
        rows,
        defaultRow: { enableDisabled, optedIn }
      } = formData;
      const groupSettings: CollectorGroupSettingsInput[] = rows.map(
        ({ enableDisabled, optedIn, ...r }) => ({
          enabled: enableDisabled === EnabledDisabled.enabled,
          canOptOut: optedIn === EnabledDisabled.enabled,
          accessScopeIn: {
            ...mapToAccessSope('departments', r.DEPARTMENT),
            ...mapToAccessSope('employeeTypes', r.EMPLOYEE_TYPE),
            ...mapToAccessSope('locations', r.LOCATION),
            ...mapToAccessSope('costCenters', r.COST_CENTER),
            ...mapToAccessSope('divisions', r.DIVISION),
            ...mapToAccessSope('serviceCenters', r.SERVICE_CENTER)
          }
        })
      );

      const input: UpdateSettingPreferencesForCollectorSettingInput = {
        installId: collectorInstallInfo!.installId!,
        settings: [
          {
            key: 'collectorEnabled',
            value: String(enableDisabled === EnabledDisabled.enabled)
          },
          {
            key: 'allowUsersToOptOutEnabled',
            value: String(optedIn === EnabledDisabled.enabled)
          }
        ],
        groupSettings: enabledGroupTypes.length ? groupSettings : []
      };

      await updateSettingPreferencesMutation({
        variables: {
          input: input
        }
      });

      onClose();
      setDefaultValues(formData);
    },
    [
      collectorInstallInfo,
      enabledGroupTypes.length,
      updateSettingPreferencesMutation,
      onClose
    ]
  );

  const dialogClasses = useMemo(() => ({ paper: classes.dialog }), [
    classes.dialog
  ]);

  return (
    <Dialog
      classes={dialogClasses}
      onClose={handleClose}
      aria-labelledby="company-collector-settings-dialog"
      open={open}
      fullWidth
      maxWidth="lg"
      data-testid="company-collector-settings-dialog"
    >
      <FormProvider {...formMethods}>
        <DialogContent className={classes.dialogContent}>
          {loading ? (
            <div data-testid="loading-skeleton-screen">
              <CompanyCollectorSettingsDialogSkeleton />
            </div>
          ) : (
            <div className={classes.container}>
              <CollectorInfo
                collector={collectorInfo}
                collectorInstallInfo={collectorInstallInfo}
                installDate={collectorInstallInfo?.date}
                onClose={onClose}
              />
              <div>
                <Divider
                  className={classes.verticalDivider}
                  orientation="vertical"
                />
              </div>
              <CollectorSettingsBody
                defaultEnabled={defaultEnabled}
                defaultOptedIn={defaultOptedIn}
                enabledGroupTypes={enabledGroupTypes}
                fields={fields}
                errors={collectorInstallErrors}
                installInfo={collectorInstallInfo}
                fieldName={'rows'}
                onClose={onClose}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                prepend={prepend}
                remove={remove}
                collectorName={collectorInstallInfo?.collectorItem?.name}
                onSave={onSubmit}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleSubmit={formMethods.handleSubmit}
                loading={loading || saving}
                isDirty={isDirty}
              />
            </div>
          )}
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
